<template>
  <div class="asc-global-filter">
    <div class="asc-global-filter--container">
      <div class="asc-global-filter--container-column1">
        <asc-label
          v-if="isOptionsDisplayed"
          value="Filter all Ascentia application data by Airline or Platform."
        ></asc-label>
        <asc-label
          v-else
          value="Filter all Ascentia application data by Platform."
        ></asc-label>
        <template v-if="isOptionsDisplayed">
          <div class="asc-global-filter--container-column1-options">
            <v-radio-group
              v-model="checkedOption"
              hide-details
              column
              @change="handleFilterChange"
            >
              <v-radio
                label="Filter By Airline"
                value="filterByAirline"
              ></v-radio>
              <v-radio
                label="Filter By Platform"
                value="filterByPlatform"
              ></v-radio>
            </v-radio-group>
          </div>
        </template>
        <div class="asc-global-filter--container-column1-filter">
          <asc-switch
            v-model="filterActive"
            label="Filter Data"
            single-line
            hide-details
          ></asc-switch>
          <div>
            <asc-label :value="getFilterDataText"></asc-label>
          </div>
        </div>
      </div>
      <div class="asc-global-filter--container-column2">
        <asc-global-filter-table
          :items="getItems"
          :filter-text="getFilterText"
          :on-update="handleUpdate"
        ></asc-global-filter-table>
      </div>
    </div>
  </div>
</template>

<script>
import { AscGlobalFilterMixin } from '@/components/mixins'
import AscLabel from '@/components/atoms/AscLabel'
import AscSwitch from '@/components/atoms/AscSwitch'
import AscGlobalFilterTable from './AscGlobalFilterTable'

export default {
  name: 'AscGlobalFilter',

  components: {
    AscLabel,
    AscSwitch,
    AscGlobalFilterTable,
  },

  mixins: [AscGlobalFilterMixin],

  props: {
    value: {
      type: Object,
      default: () => {},
    },
    resetData: {
      type: Object,
      default: () => {},
    },
    airlinesWithMasterModels: {
      type: Array,
      default: () => [],
    },
    masterModelsWithAirlines: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      allSelected: this.value.allSelected,
      checkedOption: this.value.checkedOption,
      selections: this.value?.selections || [],
      tableItems: this.getAirlinesWithMasterModels(),
      filterActive: this.value.isActive,
    }
  },

  computed: {
    getFilterText() {
      return this.checkedOption === 'filterByAirline' ? 'Platforms' : 'Airlines'
    },

    getItems() {
      return this.tableItems || []
    },

    isOptionsDisplayed() {
      return this.airlinesWithMasterModels.length > 1
    },

    getFilterDataText() {
      return this.filterActive
        ? 'Your global filter selections are turned on.'
        : 'Your global filter selections are turned "off" so all data shows within Ascentia.'
    },
  },

  watch: {
    value(newValue) {
      this.allSelected = newValue.allSelected
      this.selections = newValue.selections
      this.filterActive = newValue.isActive
      this.checkedOption = newValue.checkedOption
      this.handleFilterChange()
    },
    resetData(newValue) {
      this.allSelected = newValue.allSelected
      this.selections = newValue.selections
      this.filterActive = newValue.isActive
      this.checkedOption = newValue.checkedOption
      this.handleFilterChange()
      this.handleInput()
    },
    filterActive() {
      this.handleInput()
    },
  },

  mounted() {
    this.handleFilterChange()
    this.handleInput()
  },

  methods: {
    handleFilterChange() {
      if (this.checkedOption === 'filterByAirline') {
        this.tableItems = this.getAirlinesWithMasterModels()
      }
      if (this.checkedOption === 'filterByPlatform') {
        this.tableItems = this.getMasterModelsWithAirlines()
      }
    },

    getAirlinesWithMasterModels() {
      const selections = this.selections || this.value?.selections || []
      return this.airlinesWithMasterModels.map(airline => {
        const items = airline.items.map(item => {
          const checked =
            (this.allSelected && selections.length === 0) ||
            selections.filter(
              tableItem =>
                tableItem.airlineId === airline.airlineId &&
                tableItem.masterModelId === item.masterModelId,
            ).length > 0

          return {
            ...item,
            checked,
          }
        })
        const checked =
          (this.allSelected && selections.length === 0) ||
          selections.filter(
            tableItem => tableItem.airlineId === airline.airlineId,
          ).length > 0

        const tempItems = this.getItems || []
        const [first] = tempItems.filter(
          item => item.airlineId === airline.airlineId,
        )
        const isExpanded = first ? first.expanded : false

        return {
          ...airline,
          checked,
          expanded: isExpanded,
          items,
        }
      })
    },

    getMasterModelsWithAirlines() {
      const selections = this.selections || this.value?.selections || []
      return this.masterModelsWithAirlines.map(mm => {
        const items = mm.items.map(item => {
          const checked =
            (this.allSelected && selections.length === 0) ||
            selections.filter(
              tableItem =>
                tableItem.airlineId === item.airlineId &&
                tableItem.masterModelId === mm.masterModelId,
            ).length > 0

          return {
            ...item,
            checked,
          }
        })
        const checked =
          (this.allSelected && selections.length === 0) ||
          selections.filter(
            tableItem => tableItem.masterModelId === mm.masterModelId,
          ).length > 0

        const tempItems = this.getItems || []
        const [first] = tempItems.filter(
          item => item.masterModelId === mm.masterModelId,
        )
        const isExpanded = first ? first.expanded : false

        return {
          ...mm,
          checked,
          expanded: isExpanded,
          items,
        }
      })
    },

    handleUpdate(items) {
      this.tableItems = items
      this.selections = this.getSelections()
      this.allSelected = this.isEverythingChecked(items)
      this.handleInput()
    },

    handleInput() {
      const data = {
        allSelected: this.allSelected,
        selections: this.allSelected ? [] : this.selections,
        isActive: this.filterActive,
        checkedOption: this.checkedOption,
      }
      this.$emit('input', data)
    },
  },
}
</script>

<style lang="scss">
.asc-global-filter {
  min-height: 400px;

  &--container {
    display: flex;

    &-column1 {
      min-width: 200px;
      width: 200px;

      &-options {
        .v-input--selection-controls__input {
          transform: scale(0.8);
        }

        .v-picker {
          box-shadow: none;
        }

        .v-label {
          font-size: 14px;
          color: $primary-black;
        }
      }

      &-filter {
        margin-top: 8px;
        padding: 8px;
        background-color: $gray-0;
        padding-top: 16px;
      }
    }

    &-column2 {
      width: 100%;
      padding: 0 4px;
    }
  }
}
</style>
